import styled from '@emotion/styled';
import { uniqueId } from '@lessonup/utils';
import { rem } from 'polished';
import React, { Ref, TextareaHTMLAttributes } from 'react';
import { ComponentColors, ComponentSize, getComponentColors, spacing, spacingScaled } from '../../../foundations';
import { SpaceBetween } from '../../../utils/SpaceBetween/SpaceBetween';
import { SharedInputComponentProps } from '../input.types';
import {
  getInputFieldBorderAndColorStyles,
  getInputFieldFontStyles,
  inputFieldPaddingSize,
  inputFieldPlaceholderOpacity,
} from '../input.utils';
import { InputLabel } from '../InputLabel/InputLabel';
import { Validation } from '../Validation/Validation';

/**
 * Props derived from the native HTML textArea element, excluding conflicting attributes like 'size' and 'width',
 * which are redefined by the component.
 */
export type NativeTextAreaProps = Omit<TextareaHTMLAttributes<HTMLTextAreaElement>, 'size' | 'width' | 'disabled'>;

export interface TextAreaProps extends NativeTextAreaProps, SharedInputComponentProps {
  ref?: Ref<HTMLTextAreaElement>;
}

export const TextArea: React.FC<TextAreaProps> = (props) => {
  const {
    ref,
    id = uniqueId(`text-area-`),
    colorMode = 'neutral',
    size = 'small',
    className,
    validation,
    disabled,
    width,
    label,
    // Remaining props to be passed to the native textArea element
    ...nativeTextAreaProps
  } = props;
  const colors = getComponentColors(colorMode, { disabled, noGradient: true });

  const validationId = `${id}-validation`;
  const describedById = validation?.message ? validationId : undefined;

  return (
    <TextAreaWrapper
      direction="y"
      spacing={spacing.size4}
      colors={colors}
      $width={width}
      size={size}
      className={className}
    >
      <SpaceBetween direction="y" spacing={spacing.size2} size={size}>
        <InputLabel htmlFor={props.id} size={size}>
          {label}
        </InputLabel>
        <StyledTextArea
          {...nativeTextAreaProps}
          id={id}
          ref={ref}
          disabled={disabled}
          colors={colors}
          $size={size}
          aria-label={props['aria-label'] || label}
          aria-describedby={describedById}
          aria-invalid={validation?.type === 'negative' && true}
        />
      </SpaceBetween>
      {validation && <Validation id={validationId} size={size} colorMode={colorMode} {...validation} />}
    </TextAreaWrapper>
  );
};

const TextAreaWrapper = styled(SpaceBetween)<{ colors: ComponentColors; $width?: number; size: ComponentSize }>`
  width: ${({ $width }) => ($width ? rem($width) : 'auto')};
  position: relative;
`;

const StyledTextArea = styled.textarea<{
  colors: ComponentColors;
  $size: ComponentSize;
}>`
  ${({ colors, $size }) => getInputFieldBorderAndColorStyles(colors, $size)}
  ${({ $size }) => getInputFieldFontStyles($size)};
  resize: none;
  padding: ${({ $size }) =>
    `${rem(spacingScaled(inputFieldPaddingSize, $size))} calc(${rem(spacingScaled(inputFieldPaddingSize, $size))} + ${rem(spacingScaled(spacing.size2, $size))})`};

  ::placeholder {
    color: inherit;
    opacity: ${inputFieldPlaceholderOpacity};
  }
`;
